/**
 * OpenQuestionMasterPanel — lista de respostas em tempo real do mestre.
 *
 * Mostra todas as submissões openQuestion do slide atual, anonimizadas
 * (membershipId truncado). Ordem cronológica de chegada. Cada resposta tem um
 * botão "Exibir/Ocultar" que a libera (ou tira) do mural compartilhado que
 * todos os alunos veem (PR-4).
 */

import { useAdapter } from '../../../adapter'
import type { MissionComponentProps } from '../../../games/atlas/components'
import { avatarColor, initials } from '../responseCardUtils'
import { useLabComponentState } from './useLabComponentState'

interface Response {
  membershipId?: string
  displayName?: string
  fieldId?: string
  value?: string
  revealed?: boolean
}

export function OpenQuestionMasterPanel(props: MissionComponentProps) {
  const adapter = useAdapter()
  const slideIndex = props.slideIndex ?? 0
  const snap = useLabComponentState('lab.form.openQuestion', slideIndex)
  const responses = (snap?.aggregate.responses as Response[]) ?? []
  const total = snap?.totalPlayers ?? 0
  const count = responses.length
  const revealedCount = responses.filter((r) => r.revealed).length

  function toggleReveal(r: Response) {
    if (!r.membershipId || !r.fieldId) return
    adapter.revealOpenQuestion({
      slideIndex,
      pairId: r.fieldId,
      membershipId: r.membershipId,
      revealed: !r.revealed,
    })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <header>
        <h3 style={{ margin: 0, fontSize: '1rem' }}>Respostas em tempo real</h3>
        <p style={{ margin: '4px 0 0', opacity: 0.7, fontSize: '0.85em' }}>
          {count} / {total} aluno(s) responderam · {revealedCount} no mural
        </p>
      </header>

      {responses.length === 0 && (
        <div style={{ opacity: 0.6, fontStyle: 'italic', fontSize: '0.9em' }}>
          Aguardando respostas…
        </div>
      )}

      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {responses.map((r, i) => {
          const name = r.displayName || `Aluno ${String(r.membershipId).slice(0, 6)}`
          return (
            <li
              key={`${r.membershipId}-${i}`}
              style={{
                padding: 'var(--spacing-lab-3, 12px)',
                background: 'var(--color-lab-bg, #fff)',
                borderRadius: 'var(--radius-lab, 12px)',
                border: '1px solid var(--color-lab-rule, #D8D5CB)',
                borderLeft: r.revealed
                  ? '3px solid var(--color-lab-mint, #1E9E6A)'
                  : '3px solid var(--color-lab-rule, #D8D5CB)',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <header style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span
                  aria-hidden
                  style={{
                    width: 26,
                    height: 26,
                    flexShrink: 0,
                    borderRadius: '50%',
                    background: avatarColor(String(r.membershipId)),
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.65rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {initials(name)}
                </span>
                <span style={{ fontWeight: 600, fontSize: '0.85em', color: 'var(--color-lab-ink, #0F1115)' }}>
                  {name}
                </span>
                {r.revealed && (
                  <span style={{ marginLeft: 'auto', fontSize: '0.7em', fontWeight: 600, color: 'var(--color-lab-mint, #1E9E6A)' }}>
                    no mural
                  </span>
                )}
              </header>
              <div
                style={{
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.9em',
                  color: 'var(--color-lab-ink-2, #2A2D33)',
                  lineHeight: 1.45,
                }}
              >
                {r.value}
              </div>
              <button
                onClick={() => toggleReveal(r)}
                style={{
                  alignSelf: 'flex-start',
                  padding: '5px 12px',
                  fontSize: '0.8em',
                  fontWeight: 600,
                  border: r.revealed ? '1px solid var(--color-lab-rule, #D8D5CB)' : 'none',
                  borderRadius: 999,
                  cursor: 'pointer',
                  background: r.revealed ? 'transparent' : 'var(--color-lab-accent, #5B2DB8)',
                  color: r.revealed ? 'var(--color-lab-ink-3, #555B66)' : '#fff',
                }}
                title={r.revealed ? 'Tirar do mural' : 'Mostrar para todos os alunos'}
              >
                {r.revealed ? 'Ocultar do mural' : 'Exibir para todos'}
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
